import React, { Fragment, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  doFetchHistoricalEventLogs,
  storeHistoricalEventLogs,
  updateSinglePageLocalLogList,
  updateSinglePageSelectedEventLogIndex
} from '../../../../redux/actions';
import ViewLogModal from '../../ViewLogModal/ViewLogModal';
import { shouldShowLogsButton } from '../../ViewLogModal/utils';
import DiagnosticPackageModal, {
  canViewDiagnosticPackages,
  isDiagnosticPackagesSupported
} from '../../DiagnosticPackageModal';
import MaintenancePanelTilt from '../MaintenancePanelTilt/MaintenancePanelTilt';
import MaintenancePanelDropdownTilt from '../MaintenancePanelDropdownTilt/MaintenancePanelDropdownTilt';
import { hasCmEventLogsPermission } from '../utils';

const MenuItems = {
  VIEW_EVENT_LOGS: 'viewEvents',
  VIEW_DIAGNOSTIC_PACKAGES: 'viewDiagnostics'
};

function MaintenanceLogsPanelTilt({ device, permissions, isGa2 }) {
  const dispatch = useDispatch();
  const [eventLogsModalOpened, setEventLogsModalOpened] = useState(false);
  const [diagnosticPackagesModalOpened, setDiagnosticPackagesModalOpened] = useState(false);
  const eventLogsAvailable = shouldShowLogsButton(device, hasCmEventLogsPermission(permissions));
  const diagnosticPackagesAvailable = isDiagnosticPackagesSupported(device) && canViewDiagnosticPackages(permissions);
  const handleEventLogsModalOpen = useCallback(() => {
    setEventLogsModalOpened(true);
    dispatch(updateSinglePageSelectedEventLogIndex(device.id, 0));
    dispatch(storeHistoricalEventLogs(device.id, []));
    dispatch(
      doFetchHistoricalEventLogs(
        device.id,
        0,
        100,
        'timestamp',
        'desc',
        (result) => {
          dispatch(storeHistoricalEventLogs(device.id, result));
          dispatch(updateSinglePageLocalLogList(result?.['0']?.events ?? []));
        },
        (error) => {
          console.log('doUpdateHistoricalEventLogsError', error);
        }
      )
    );
  }, [dispatch, device]);
  const handleEventLogsModalClose = useCallback(() => {
    setEventLogsModalOpened(false);
  }, []);
  const handleDiagnosticPackagesModalOpen = useCallback(() => {
    setDiagnosticPackagesModalOpened(true);
  }, []);
  const handleDiagnosticPackagesModalClose = useCallback(() => {
    setDiagnosticPackagesModalOpened(false);
  }, []);
  const handleSelect = useCallback(
    (id) => {
      switch (id) {
        case MenuItems.VIEW_EVENT_LOGS:
          return handleEventLogsModalOpen();
        case MenuItems.VIEW_DIAGNOSTIC_PACKAGES:
          return handleDiagnosticPackagesModalOpen();
        default:
          return;
      }
    },
    [handleEventLogsModalOpen, handleDiagnosticPackagesModalOpen]
  );

  const eventLogsModal = eventLogsModalOpened && (
    <ViewLogModal
      deviceId={device?.id}
      deviceConnectionState={device?.connectionState}
      onClose={handleEventLogsModalClose}
    />
  );
  const diagnosticPackagesModal = diagnosticPackagesModalOpened && (
    <DiagnosticPackageModal deviceId={device.id} onClose={handleDiagnosticPackagesModalClose} />
  );

  if (eventLogsAvailable && diagnosticPackagesAvailable) {
    return (
      <Fragment>
        <MaintenancePanelDropdownTilt
          id="maintenanceBarLogsTile"
          title="Logs"
          dropdownIds={[MenuItems.VIEW_EVENT_LOGS, MenuItems.VIEW_DIAGNOSTIC_PACKAGES]}
          dropdownData={['Event Log', 'Diagnostic']}
          handleMBarSelection={handleSelect}
          disabled={isGa2}
          hideArrow
        />
        {eventLogsModal}
        {diagnosticPackagesModal}
      </Fragment>
    );
  }

  if (eventLogsAvailable) {
    return (
      <Fragment>
        <MaintenancePanelTilt
          id="maintenanceBarViewEventsTile"
          title="View Events"
          selectionId={MenuItems.VIEW_EVENT_LOGS}
          handleMBarSelection={handleSelect}
          disabled={isGa2}
        />
        {eventLogsModal}
      </Fragment>
    );
  }

  if (diagnosticPackagesAvailable) {
    return (
      <Fragment>
        <MaintenancePanelTilt
          id="maintenanceBarViewDiagnosticsTile"
          title="View Diagnostics"
          selectionId={MenuItems.VIEW_DIAGNOSTIC_PACKAGES}
          handleMBarSelection={handleSelect}
          disabled={isGa2}
        />
        {diagnosticPackagesModal}
      </Fragment>
    );
  }

  return <MaintenancePanelDropdownTilt id="maintenanceBarLogsTile" title="Logs" disabled hideArrow />;
}

export default MaintenanceLogsPanelTilt;
